// JSON Schema equivalents of the production pipeline's structured-output schemas
// (trackable-api src/lib/langgraph/stages/*.ts), plus the single-prompt schema. Field
// descriptions are copied verbatim: the model reads them. Last synced 2026-09-24.

type JsonSchema = Record<string, unknown>

const nullableString = (description: string): JsonSchema => ({ type: [`string`, `null`], description })

const object = (properties: Record<string, JsonSchema>): JsonSchema => ({
  type: `object`,
  properties,
  required: Object.keys(properties),
  additionalProperties: false,
})

export const CHANNELS = [`Digital Marketing`, `Referral Marketing`, `Traditional Marketing`, `No Channel`]

export const CALL_TYPES = [
  `appointment_request`,
  `appointment_confirmation`,
  `appointment_reschedule`,
  `appointment_cancellation`,
  `general_inquiry`,
  `emergency`,
  `prescription_question`,
  `records_request`,
  `referral_request`,
  `interoffice`,
  `wrong_number`,
  `voicemail`,
  `missed_call`,
  `other`,
]

export const TREATMENTS = [
  `Fillings`,
  `Crown`,
  `Root canal`,
  `Dental implants`,
  `Orthodontics`,
  `Tooth extractions`,
  `Veneers`,
  `Dentures`,
  `Teeth whitening`,
  `Bonding`,
  `General dentistry`,
  `Cosmetic dentistry`,
  `Dental cleaning`,
  `Dental sealants`,
  `Mouth reconstruction`,
  `Periodontology`,
  `Dental bridges`,
  `Implants`,
  `Dental checkup`,
  `Inlays and onlays`,
  ``,
]

/** The callOutcomes keys, in production's order (types/CallDataExtraction.ts). */
export const OUTCOMES = [
  `Appointment Confirmation`,
  `Appointment Scheduled`,
  `Appointment Rescheduled`,
  `Appointment Not Scheduled`,
  `Canceled Appointment`,
  `Prescription Question`,
  `Wrong Number`,
  `Voicemail`,
  `Missed Call`,
  `Interoffice Request`,
  `Referral Request`,
  `Request for Records`,
  `Callback`,
  `Insurance: State / HMO`,
  `Office Too Far`,
  `Service Not Offered`,
  `Emergency`,
  `Other`,
  `Insurance: Out of Network`,
  `New Patient`,
]

const PATIENT_TYPE: JsonSchema = {
  type: `string`,
  enum: [`New Patient`, `Current Patient`, `Non-Patient`, `Other`],
  description: `New Patient=calling about becoming a patient. Current Patient=existing patient. Non-Patient=calling on behalf of someone else without seeking services for themselves. Other=none apply.`,
}

const OVERVIEW_FIELDS = {
  answerer: nullableString(
    `Name of the staff member who answered the call. Null if not stated or if it goes to voicemail.`,
  ),
  summary: { type: `string`, description: `A concise 1-3 sentence summary of the call.` },
  channel: {
    type: `string`,
    enum: CHANNELS,
    description: `How the caller found the practice. Digital=online/Google/social. Referral=person/insurance/provider. Traditional=billboard/radio/TV. No Channel=not mentioned.`,
  },
  callType: { type: `string`, enum: CALL_TYPES, description: `The primary purpose of the call.` },
}

const PERSON_FIELDS = {
  name: nullableString(`Birth name of the person. If spelled out, use that spelling. Null if not mentioned.`),
  isCaller: { type: `boolean`, description: `Is this person the one who made the call?` },
  relationship: nullableString(
    `Relationship to the caller if not the caller themselves (e.g., "son", "spouse", "mother"). Null if none.`,
  ),
  patientType: PATIENT_TYPE,
  phone: nullableString(`Phone number if explicitly provided. Null if not mentioned.`),
  email: nullableString(`Email address if explicitly provided. Null if not mentioned.`),
  birthDate: nullableString(
    `Birth date in YYYY-MM-DD format if mentioned. Two-digit years should be interpreted as 2000s. Null if not mentioned.`,
  ),
}

const PEOPLE_DESCRIPTION = `All people discussed in the call. Always include the caller. Never include staff members. If caller is calling about someone else (e.g., "my son", "my daughter"), include BOTH the caller AND that person as separate entries.`

const APPOINTMENT_FIELDS = {
  appointmentRequested: {
    type: `boolean`,
    description: `Did this person (or someone on their behalf) ask to schedule an appointment?`,
  },
  appointmentScheduled: { type: `boolean`, description: `Was an appointment actually scheduled for this person?` },
  appointmentDate: nullableString(`Date of appointment in YYYY-MM-DD format if scheduled. Null if not scheduled.`),
  appointmentTime: nullableString(
    `Time of appointment in h:mm a format (e.g., 4:00 pm) if scheduled. Null if not scheduled.`,
  ),
  treatment: {
    type: [`string`, `null`],
    enum: [...TREATMENTS, null],
    description: `Treatment type if mentioned. Null if not mentioned.`,
  },
  serviceRequested: nullableString(`Specific service requested if mentioned. Null if not mentioned.`),
  reasonForNotScheduling: nullableString(
    `Why appointment was not scheduled if requested but not scheduled. Null otherwise.`,
  ),
}

const OUTCOME_FIELDS = {
  outcomes: {
    type: `array`,
    items: { type: `string`, enum: OUTCOMES },
    description: `Applicable outcomes for this person. Multiple can apply.`,
  },
  insuranceProvider: nullableString(`Insurance provider if mentioned. Null if not mentioned.`),
  referral: nullableString(`How they heard about the practice if mentioned. Null if not mentioned.`),
}

const PERSON_INDEX = { type: `number`, description: `Index of the person in the people array (0-based).` }

export const OVERVIEW_SCHEMA = object(OVERVIEW_FIELDS)

export const PEOPLE_SCHEMA = object({
  people: { type: `array`, minItems: 1, items: object(PERSON_FIELDS), description: PEOPLE_DESCRIPTION },
})

export const APPOINTMENT_SCHEMA = object({
  appointments: { type: `array`, items: object({ personIndex: PERSON_INDEX, ...APPOINTMENT_FIELDS }) },
})

export const OUTCOME_SCHEMA = object({
  outcomes: { type: `array`, items: object({ personIndex: PERSON_INDEX, ...OUTCOME_FIELDS }) },
})

export const ACTION_ITEMS_SCHEMA = object({
  actionItems: nullableString(`Recommended follow-up actions for front desk staff.`),
})

/** The single-prompt (v2) answer: every stage's fields in one object, people carrying
 *  their own appointment and outcome fields. Descriptions are the stages' own. */
export const SINGLE_SCHEMA = object({
  ...OVERVIEW_FIELDS,
  people: {
    type: `array`,
    minItems: 1,
    items: object({ ...PERSON_FIELDS, ...APPOINTMENT_FIELDS, ...OUTCOME_FIELDS }),
    description: PEOPLE_DESCRIPTION,
  },
  actionItems: nullableString(
    `Recommended follow-up actions for front desk staff. Null unless an outcome is Appointment Not Scheduled, Callback or Voicemail.`,
  ),
})
